import ReactDOMServer from "react-dom/server";
import L from "leaflet";
import momentTZ from "moment-timezone";
import {
  userDataObject,
  getPlantCoordinates,
  statusColorMap,
  rackColorSortOrder,
  transformMap
} from "./constants";
import { getBlobStorageEndPoint } from "../network/apiUrl";
import { highlight } from "../../components/FilterDrawer/utils";
import { store } from "../../store";
import { msalConfig } from "../../authConfig";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

import lgvGreen from "../../assets/images/lgvMarkerImage/lgvGreen.png";
import lgvBlack from "../../assets/images/lgvMarkerImage/lgvBlack.png";
import lgvBlue from "../../assets/images/lgvMarkerImage/lgvBlue.png";
import lgvLightBlue from "../../assets/images/lgvMarkerImage/lgvLightBlue.png";
import lgvMaroon from "../../assets/images/lgvMarkerImage/lgvMaroon.png";
import lgvRed from "../../assets/images/lgvMarkerImage/lgvRed.png";
import lgvYellow from "../../assets/images/lgvMarkerImage/lgvYellow.png";

// if add the plant in the array to show on site selection map
// prettier-ignore
const plantsData = [
  { name: "Alabama", code: "ALA", useNewPointerLogic: true },
  { name: "Atlanta", code: "ATL", useNewPointerLogic: true },
  { name: "Bakersfield", code: "BAK", useNewPointerLogic: true },
  { name: "Baltimore", code: "BAL", useNewPointerLogic: true },
  { name: "Baytown", code: "BAY", useNewPointerLogic: true },
  { name: "Bloomfield", code: "BLM", useNewPointerLogic: true },
  { name: "Carolina", code: "CAR", useNewPointerLogic: true },
  { name: "Cincinnatti", code: "CIN", useNewPointerLogic: true },
  { name: "Columbus", code: "COL", useNewPointerLogic: true },
  { name: "Detroit", code: "DET", useNewPointerLogic: true },
  { name: "Guadalajara", code: "GDL", useNewPointerLogic: true },
  { name: "Hazleton", code: "HAZ", useNewPointerLogic: true },
  { name: "Houston", code: "HOU", useNewPointerLogic: true },
  { name: "Jacksonville", code: "JAX", useNewPointerLogic: true },
  { name: "Kansas City 2", code: "KC2", useNewPointerLogic: true },
  { name: "Kansas City", code: "KNC", useNewPointerLogic: true },
  { name: "Kenosha", code: "KEN", useNewPointerLogic: true },
  { name: "Lancaster", code: "LAN", useNewPointerLogic: true },
  { name: "Las Vegas", code: "LAS", useNewPointerLogic: true },
  { name: "Louisville", code: "LOU", useNewPointerLogic: true },
  { name: "Mesa", code: "MES", useNewPointerLogic: true },
  { name: "Miami", code: "MIA", useNewPointerLogic: true },
  { name: "Mississippi", code: "MIS", useNewPointerLogic: true },
  { name: "Minnesota", code: "MIN", useNewPointerLogic: true },
  { name: "Mooresville", code: "MOR", useNewPointerLogic: true },
  { name: "Mexico City", code: "MXC", useNewPointerLogic: true },
  { name: "Monterrey", code: "MTY", useNewPointerLogic: true },
  { name: "New Orleans", code: "NOR", useNewPointerLogic: true },
  { name: "Oklahoma City", code: "OKC", useNewPointerLogic: true },
  { name: "Pittsburgh", code: "PIT", useNewPointerLogic: true },
  { name: "Plainfield", code: "PLA" },
  { name: "Rialto", code: "RIA", useNewPointerLogic: true },
  { name: "Richmond", code: "RCH", useNewPointerLogic: true },
  { name: "Salt Lake City 2", code: "SL2", useNewPointerLogic: true },
  { name: "San Antonio", code: "SAN", useNewPointerLogic: true },
  { name: "Stockton 3", code: "ST3", useNewPointerLogic: true },
  { name: "Stockton 4", code: "ST4", useNewPointerLogic: true },
  { name: "Tacoma", code: "TAC", useNewPointerLogic: true },
  { name: "Temple", code: "TEM", useNewPointerLogic: true }
];

export const filterEnabledPlants = (data) =>
  data.filter(({ code }) => plantsData.map(({ code }) => code).includes(code));

export const getAvailablePlant = (locationCode) =>
  plantsData.map(({ code }) => code).includes(locationCode);

export const getPlantName = (code) =>
  plantsData.filter((el) => el.code === code)?.[0]?.name;

export const isBlockRackNonLgvEnabled = (plantCode) =>
  plantsData.map(({ code }) => code).includes(plantCode);

export const usesNewPointerLogic = (plantCode) =>
  plantsData.find(({ code }) => code === plantCode)?.useNewPointerLogic ??
  false;

export const statusColorRenderer = (status) => {
  return statusColorMap[status] ?? "#000000";
};

export const externalLinkNavigator = (link, plantCode) => {
  const quickLinks = {
    logistics: `https://app.powerbi.com/groups/me/reports/64fa4bc6-35a9-48ea-bdf1-af49555a6d7f/ReportSectionc1d9f7cba778eee595ac?ctid=773bda68-87c6-401a-b9a4-3dfb1f92e189`,
    performance: `http://pdc-whse-perf:8080/d/wWbpz254z/lgv_one_summary?orgId=1&refresh=1m&var-WHSE=${plantCode}_LGV`,
    shipment: `http://pdc-whse-perf:8080/d/G7En7Wp4z/shipment-progress?orgId=1&refresh=5s&var-WHSE=${plantCode}_LGV`,
    downtime: `http://pdc-whse-perf:8080/d/yOxxzbWSk/lgv-alarm-summary?orgId=1&var-WHSE=${plantCode}_LGV&var-LGV_ID=All`,
    pairing: `http://pdc-whse-perf:8080/d/Tdz-8g84k/active-mixed-pairing?orgId=1&refresh=5s`
  };
  return quickLinks[link];
};

/******************DOCK DOOR Utils*******************************/
export const dockDoorStatusColor = (status) => {
  if (!status) return "#FFFFFF";
  return statusColorMap[status];
};

export const findDockDoorPosition = (plantConfig) =>
  plantConfig?.indoorMap["dockDoors"]?.position;

export const getAdjustedLineProductionCoordinates = () => {
  let levels = [
    { start: 500, end: 800, level: { x: 75, y: 170 } },
    { start: 801, end: 1200, level: { x: 240, y: 170 } },
    { start: 1201, end: 1400, level: { x: 75, y: 170 } },
    { start: 1401, end: 1600, level: { x: 60, y: 170 } },
    { start: 1601, end: 1800, level: { x: 60, y: 170 } },
    { start: 1801, end: 2000, level: { x: 110, y: 170 } },
    { start: 2001, end: 2200, level: { x: 230, y: 170 } },
    { start: 2201, end: 2400, level: { x: 610, y: 170 } },
    { start: 2401, end: 2600, level: { x: 650, y: 170 } },
    { start: 2601, end: 2800, level: { x: 550, y: 170 } }
  ];
  let windowWidth = window.innerWidth;
  let matchingWindowLevel = levels.find(
    (level) => windowWidth > level.start && windowWidth < level.end
  );
  return matchingWindowLevel;
};

export const setDynamicZoomLevelToMap = (scaleLevel, mapType) => {
  const config = transformMap[scaleLevel] || transformMap["1.0"];

  const idMap = {
    indoor: "indoor-map-id-reference",
    timeMachine: "time-machine-map-id-reference"
  };

  const targetId = idMap[mapType];
  if (!targetId) return;

  const el = document.getElementById(targetId);
  if (!el) return;

  const {
    scale,
    translate: [x, y]
  } = config;
  el.style.transform = `scale(${scale}) translate(${x}px, ${y}px)`;
};

export const isScreenBigger = () => {
  if (Math.round(((window.outerWidth - 10) / window.innerWidth) * 100) <= 50) {
    return true;
  }
  return false;
};

export const getHeatmapWeightage = (length) => {
  if (length < 1) {
    return { fillOpacity: "5%", fillColor: "white" };
  } else if (length <= 1 && length < 10) {
    return { fillOpacity: "10%", fillColor: "red" };
  } else if (length >= 10 && length < 15) {
    return { fillOpacity: "15%", fillColor: "red" };
  } else if (length >= 15 && length < 20) {
    return { fillOpacity: "25%", fillColor: "red" };
  } else if (length >= 25 && length < 30) {
    return { fillOpacity: "45%", fillColor: "red" };
  } else if (length >= 30) {
    return { fillOpacity: "65%", fillColor: "red" };
  }
};

export const timeFrameFormatter = (timeMachineTimeFrames) => {
  let convertedFromDate = `${timeMachineTimeFrames?.fromDate}T${timeMachineTimeFrames?.fromTime}:00.000Z`;
  let convertedToDate = `${timeMachineTimeFrames?.toDate}T${timeMachineTimeFrames?.toTime}:00.000Z`;
  const { fromTimePlant, toTimePlant } = convertLocaleToPlantTimeZone(
    convertedFromDate,
    convertedToDate
  );
  return { fromTimePlant, toTimePlant };
};


export const getLocalStorageItem = (objectName) => {
  if (localStorage.getItem(objectName) !== undefined) {
    return JSON.parse(localStorage.getItem(objectName));
  }
};

export const setLocalStorageItem = (objectName, value) => {
  localStorage.setItem(objectName, JSON.stringify(value));
};

export const clearLocalStorageItem = (objectName) => {
  localStorage.removeItem(objectName);
};

export const reloadApp = () => window.location.reload();

export const timeStampMinuteToHourFormatter = (time) => {
  let totalTimeParts = time.split(":");
  if (totalTimeParts.length === 2) {
    return `${totalTimeParts[0]}:${totalTimeParts[1]}`;
  } else {
    return `${totalTimeParts[1]}:${totalTimeParts[2]}`;
  }
};

export const formatTime = (inputTime) => {
  const [hoursStr, minutesStr, secondsStr] = inputTime.split(":");
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);
  const seconds = parseInt(secondsStr, 10);

  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");
  const formattedSeconds = seconds.toString().padStart(2, "0");

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export const convertLocaleToPlantTimeZone = (
  convertedFromDate,
  convertedToDate
) => {
  if (convertedFromDate && convertedToDate) {
    let fromDate = new Date(convertedFromDate);
    let toDate = new Date(convertedToDate);
    const timeZone = store.getState().plant.plantDetails.plantTimeZone;

    const plantTimeZoneFromDate = fromDate.toLocaleString("en-US", {
      timeZone: timeZone,
      timeStyle: "medium",
      dateStyle: "medium",
      hourCycle: "h24"
    });
    const plantTimeZoneToDate = toDate.toLocaleString("en-US", {
      timeZone: timeZone,
      timeStyle: "medium",
      dateStyle: "medium",
      hourCycle: "h24"
    });

    const convert = (dateAndTime) => {
      return new Date(dateAndTime).toISOString();
    };

    let fromTimePlant = convert(plantTimeZoneFromDate);
    let toTimePlant = convert(plantTimeZoneToDate);

    return { fromTimePlant, toTimePlant };
  }
};

export const expireAuthToken = () => {
  localStorage.clear();
  reloadApp();
};

/**
 * Up to Down
 * Increase the count, go down
 * Decrease the count, go up
 * Left to Right
 * Increase to count, go
 * Decrease the count, go
 */

export const getBottomTopCoordinates = () => ({
  rectBottomLeftXNew: 0,
  rectBottomLeftYNew: 500,
  rectTopRightXNew: 1446,
  rectTopRightYNew: 5.453125
});

export const lgvNameRenderer = (vehicleNumber) =>
  vehicleNumber.includes("LGV") ? vehicleNumber.split("V")[1] : vehicleNumber;

export const trafficLgvDataGrouping = (trafficData) => {
  let trafficObject = {};
  trafficData.forEach((data, index) => {
    let objectName = data.LGV_ID;
    if (!trafficObject[objectName]) {
      trafficObject[objectName] = [];
    }
    trafficObject[objectName].push(data);
  });
  return trafficObject;
};

export const mapCoordinate = (
  coordinateX,
  coordinateY,
  renderObject,
  bounds
) => {
  const plantCode = store.getState().plant.plantDetails.plantCode;

  if (plantCode) {
    let startX;
    let startY;
    let endX;
    let endY;

    if (bounds !== undefined) {
      startX = bounds.startX;
      startY = bounds.startY;
      endX = bounds.endX;
      endY = bounds.endY;
    } else {
      const { rectBottomLeftX, rectBottomLeftY, rectTopRightX, rectTopRightY } =
        getPlantCoordinates(plantCode, renderObject);
      startX = rectBottomLeftX;
      startY = rectBottomLeftY;
      endX = rectTopRightX;
      endY = rectTopRightY;
    }

    const {
      rectBottomLeftXNew,
      rectBottomLeftYNew,
      rectTopRightXNew,
      rectTopRightYNew
    } = getBottomTopCoordinates();

    let ratioX = (coordinateX - startX) / (endX - startX);
    let ratioY = (coordinateY - startY) / (endY - startY);

    let translatedX =
      rectBottomLeftXNew + ratioX * (rectTopRightXNew - rectBottomLeftXNew);
    let translatedY =
      rectBottomLeftYNew + ratioY * (rectTopRightYNew - rectBottomLeftYNew);

    return {
      x: translatedX,
      y: translatedY
    };
  } else {
    return { x: 0, y: 0 };
  }
};

export const convertReactComponentToHtmlIcon = (
  reactComponent,
  className = "",
  iconSize,
  iconAnchor = null
) =>
  L.divIcon({
    className,
    iconSize,
    iconAnchor,
    html: ReactDOMServer.renderToString(reactComponent)
  });

export const convertReactComponentToStringHtml = (reactComponent) => {
  return ReactDOMServer.renderToString(reactComponent);
};

export const convertTimestampIntoHHMM = (timeStamp) => {
  if (!timeStamp) return "";
  let date = timeStamp.split("T");
  let time = date[1].split("Z");
  return `${date[0]} ${time[0]}`.split(":").slice(0, 2).join(":");
};

//Combine Kafka LGV data and Table LGV data
export const combineLGVDetailWithCoordinateData = (
  lgvCoordinates,
  lgvDetails
) => {
  const lgvCoordinatesData = lgvCoordinates.filter(
    (lgv) => lgv.latitude !== "undefined" && lgv.longitude !== "undefined"
  );

  if (lgvCoordinatesData == "NO_DATA" || lgvCoordinatesData == undefined)
    return lgvDetails;

  lgvDetails.forEach((lgv) => {
    lgvCoordinatesData.forEach((vehicle) => {
      const { vehicleNumber, latitude, longitude } = vehicle;
      let lgvNumber =
        typeof vehicleNumber === "string" && vehicleNumber.includes("V")
          ? vehicleNumber.split("V")[1]
          : vehicleNumber.toString();
      if (lgv.vehicleNumber === lgvNumber) {
        lgv.latitude = latitude;
        lgv.longitude = longitude;
      }
    });
  });
  return lgvDetails;
};

export const checkIsLocationCodeValid = (locationCode, locationCodeLocal) => {
  if (locationCode !== "" || locationCodeLocal !== null) {
    return true;
  } else {
    return false;
  }
};

export const qualityStatusBadgeRenderer = (status) => {
  switch (status.toLowerCase()) {
    case "available":
      return "available-pallet";
    case "hold":
      return "hold-pallet";
    case "manuallabel":
      return "manual-pallet";
    case "scrap":
      return "scrap-pallet";
    case "shipped":
      return "shipped-pallet";
    case "removed":
      return "removed-pallet";
    case "empsale":
      return "empsale-pallet";
    case "nonconform":
      return "non-conform-pallet";
    case "received":
      return "received-pallet";
    case "quality":
      return "quality-pallet";
    case "destruction":
      return "destruction-pallet";
    case "consumed":
      return "consumed-pallet";
    case "error":
      return "error-pallet";
    case "incubation":
      return "incubation-pallet";
    default:
      return "null-pallet";
  }
};

const rotatePalletTextBasedOnRotation = (angle) => {
  if (angle == -90) {
    return "pallet-rotate-negative-90";
  }
  if (angle == 90) {
    return "pallet-rotate-90";
  }
  return "";
};

export const blockRackTableDataValidation = (value) => {
  if (value) {
    if (value !== null && value.length !== 0) {
      return value;
    } else {
      return "null";
    }
  } else {
    return "";
  }
};

const getPalletColor = ({
  storageDisabled,
  retrievalDisabled,
  positionStorageBlocked,
  positionRetrievalBlocked,
  positionStorageDisabled,
  positionRetrievalDisabled,
  count,
  stackHeight,
  totalCount
}) => {
  const isLocationStorageOrRetrievalBlocked =
    retrievalDisabled || storageDisabled;
  const isPositionStorageOrRetrievalBlocked =
    positionStorageBlocked || positionRetrievalBlocked;
  const isNotPositonStorageOrRetrievalBlocked =
    !positionStorageBlocked && !positionRetrievalBlocked;
  const isNeitherBlockedOrDisabled =
    isNotPositonStorageOrRetrievalBlocked &&
    !positionStorageDisabled &&
    !positionRetrievalDisabled &&
    !retrievalDisabled &&
    !storageDisabled;

  if (isPositionStorageOrRetrievalBlocked) return "red";
  else if (
    isNotPositonStorageOrRetrievalBlocked &&
    (isLocationStorageOrRetrievalBlocked ||
      positionStorageDisabled ||
      positionRetrievalDisabled)
  )
    return "pink";
  else if (isNeitherBlockedOrDisabled) {
    if (count === 0) return "grey";

    if ((stackHeight && count < stackHeight) || count < totalCount)
      return "green";

    if ((stackHeight && count === stackHeight) || count === totalCount)
      return "blue";

    return "yellow";
  }
};

export const flattenBlockData = (data) => {
  return Object.values(data).flatMap((rows) =>
    Object.values(rows).flatMap((pallets) => pallets)
  );
};

export const getBlockRackEmptyPositions = (data, isRackModal) => {
  const selectedData = isRackModal ? data : flattenBlockData(data);
  if (!Array.isArray(selectedData)) return 0;
  return selectedData.filter(
    (item) =>
      !item.storageDisabled &&
      !item.positionStorageDisabled &&
      !item.positionStorageBlocked &&
      !item.lpn
  ).length;
};

export const getBlockOrRackTotalPalletData = (data, isRackModal) => {
  const selectedData = isRackModal ? data : flattenBlockData(data);
  return selectedData.filter(({ lpn }) => lpn).length;
};

//key == storageDisabled || retrivalDisabled
export const isStorageOrRetrievalEnabled = (data, key, isRackModal) => {
  const selectedData = isRackModal ? data : flattenBlockData(data);
  const anyDisabled = selectedData.some((item) => item[key] === 1);
  return anyDisabled ? "NO" : "YES";
};

export const onDragStart = (event) => {
  if (document.body.classList.contains("no-drag")) return;
  event.currentTarget.classList.add("active-shipment-status-container-on-drag");
};

export const onDragEnd = (event) => {
  if (document.body.classList.contains("no-drag")) return;
  event.currentTarget.classList.remove(
    "active-shipment-status-container-on-drag"
  );
};

export const onDragEnter = (event) => {
  event.currentTarget.classList.add("drag-over");
};

export const onDragLeave = (event) => {
  event.currentTarget.classList.remove("drag-over");
};

export const replaceDotWithDash = (str) => {
  return str.replaceAll(".", "-");
};

export const checkIsPalletPositionValid = (totalPalletList, pallet, index) => {
  //Current Pallet Data
  let palletData;
  if (pallet[1]) {
    palletData = pallet[1][0];
  } else if (pallet[2]) {
    palletData = pallet[2][0];
  } else if (pallet[3]) {
    palletData = pallet[3][0];
  }

  if (index === 0) {
    if (palletData.palletPosition !== 1) {
      return false;
    }
  } else {
    let palletGroup;
    //Get the previous Pallet data
    if (totalPalletList[index - 1][1]) {
      palletGroup = totalPalletList[index - 1][1][0];
    } else if (totalPalletList[index - 1][2]) {
      palletGroup = totalPalletList[index - 1][2][0];
    } else if (totalPalletList[index - 1][3]) {
      palletGroup = totalPalletList[index - 1][3][0];
    }
    //Check current pallet position is n + 1th pallet position
    if (palletGroup.palletPosition + 1 === palletData.palletPosition) {
      return false;
    } else {
      return true;
    }
  }
};

export const normalizeAndPreFillEmptyPalletPosition = (data) => {
  const normalized = {};

  let maxRowCount = 0;
  for (const topKey in data) {
    const childKeys = Object.keys(data[topKey]).map(Number).filter((n) => !isNaN(n));
    const maxKey = childKeys.length ? Math.max(...childKeys) : 0;
    if (maxKey > maxRowCount) maxRowCount = maxKey;
  }
  if (maxRowCount < 2) maxRowCount = 2;

  for (const topKey in data) {
    const child = data[topKey];
    const childKeys = Object.keys(child);

    normalized[topKey] = { ...child };

    if (childKeys.length < maxRowCount) {
      const templateKey = childKeys[0];
      const originalArray = child[templateKey];

      for (let row = 1; row <= maxRowCount; row++) {
        const rowKey = String(row);
        if (!childKeys.includes(rowKey)) {
          const emptyPalletObject = {
            row: row,
            lpn: "",
            sku: "",
            lot: "",
            quantity: null,
            qualityStatus: "",
            count: 0,
            expirationDate: null,
            productionDate: null,
            palletStatus: "empty"
          };

          if (Array.isArray(originalArray)) {
            const copiedArray = originalArray.map((item) => ({
              ...item,
              ...emptyPalletObject
            }));
            normalized[topKey][rowKey] = copiedArray;
          } else {
            normalized[topKey][rowKey] = [emptyPalletObject];
          }
        }
      }
    }
  }

  return normalized;
};

export const preFillPalletPositionIfEmpty = (renderList) => {
  if (renderList) {
    const palletPositions = renderList.map((item) => {
      if (item[1]) return item[1][0]?.palletPosition;
      else if (item[2]) return item[2][0]?.palletPosition;
      return 0;
    });

    // Check is the list is reversed
    const isAscending =
      palletPositions[0] < palletPositions[palletPositions.length - 1];

    // Find the missing pallet position
    let missingPalletPosition;
    for (let i = 1; i <= Math.max(...palletPositions); i++) {
      if (!palletPositions.includes(i)) {
        missingPalletPosition = i;
        break;
      }
    }

    // Find the correct index to insert the missing pallet position
    let insertIndex;
    if (isAscending) {
      for (let i = 0; i < palletPositions.length; i++) {
        if (palletPositions[i] > missingPalletPosition) {
          insertIndex = i;
          break;
        }
      }
    } else {
      for (let i = 0; i < palletPositions.length; i++) {
        if (palletPositions[i] < missingPalletPosition) {
          insertIndex = i;
          break;
        }
      }
    }

    // Fill the missing pallet
    const newData = {};
    let inserted = false;
    let j = 0;
    for (let i = 0; i < palletPositions.length; i++) {
      if (i === insertIndex) {
        newData[j] = {
          palletPosition: missingPalletPosition,
          palletStatus: "empty"
        };
        j++;
        inserted = true;
      }
      newData[j] = renderList[i];
      j++;
    }
    // Do not add the new data to the end if the list is in reverse order
    if (isAscending && !inserted) {
      newData[j] = {
        palletPosition: missingPalletPosition,
        palletStatus: "empty"
      };
    }

    return newData;
  }
};

export const renderSinglePalletLocation = (pallet, index, blocksCount, rowCount = 2) => {
  if (pallet.palletStatus) {
    return renderDoubleEmptyPalletLocation(index, rowCount);
  } else {
    return (
      <div key={index} className="pallet-row">
        {pallet[1] && pallet[1].length !== 0 ? (
          <div
            className={`pallet pallet-single ${getPalletColor(
              blocksCount[pallet[1][0].location][pallet[1][0].row][
                pallet[1][0].palletPosition
              ]
            )}${highlight(pallet[1])}`}
          >
            <p
              className={`${rotatePalletTextBasedOnRotation(
                blocksCount[pallet[1][0].location][pallet[1][0].row][
                  pallet[1][0].palletPosition
                ].angle
              )}`}
            >
              {/* {pallet[1][0].palletLevel} */}
              {
                blocksCount[pallet[1][0].location][pallet[1][0].row][
                  pallet[1][0].palletPosition
                ].count
              }
            </p>
          </div>
        ) : null}
        {pallet[2] && pallet[2].length !== 0 ? (
          <div
            className={`pallet pallet-single ${getPalletColor(
              blocksCount[pallet[2][0].location][pallet[2][0].row][
                pallet[2][0].palletPosition
              ]
            )} ${highlight(pallet[2])}`}
          >
            <p
              className={`${rotatePalletTextBasedOnRotation(
                blocksCount[pallet[2][0].location][pallet[2][0].row][
                  pallet[2][0].palletPosition
                ].angle
              )}`}
            >
              {/* {pallet[2][0].palletLevel} */}
              {
                blocksCount[pallet[2][0].location][pallet[2][0].row][
                  pallet[2][0].palletPosition
                ].count
              }
            </p>
          </div>
        ) : null}
        {pallet[3] && pallet[3].length !== 0 ? (
          <div
            className={`pallet pallet-single ${getPalletColor(
              blocksCount[pallet[3][0].location][pallet[3][0].row][
                pallet[3][0].palletPosition
              ]
            )} ${highlight(pallet[3])}`}
          >
            <p
              className={`${rotatePalletTextBasedOnRotation(
                blocksCount[pallet[3][0].location][pallet[3][0].row][
                  pallet[3][0].palletPosition
                ].angle
              )}`}
            >
              {/* {pallet[3][0].palletLevel} */}
              {
                blocksCount[pallet[3][0].location][pallet[3][0].row][
                  pallet[3][0].palletPosition
                ].count
              }
            </p>
          </div>
        ) : null}
      </div>
    );
  }
};

export const renderDoublePalletLocation = (pallet, index, blocksCount, rowCount = 2) => {
  if (pallet.palletStatus) {
    return renderSingleEmptyRowPalletLocation(index, rowCount);
  } else {
    return (
      <div key={index} className="pallet-col">
        {pallet[1] && pallet[1].length !== 0 && (
          <div
            className={`pallet pallet-double ${getPalletColor(
              blocksCount[pallet[1][0].location][pallet[1][0].row][
                pallet[1][0].palletPosition
              ]
            )} ${highlight(pallet[1])}`}
          >
            <p
              className={`${rotatePalletTextBasedOnRotation(
                blocksCount[pallet[1][0].location][pallet[1][0].row][
                  pallet[1][0].palletPosition
                ].angle
              )}`}
            >
              {
                blocksCount[pallet[1][0].location][pallet[1][0].row][
                  pallet[1][0].palletPosition
                ].count
              }
            </p>
          </div>
        )}
        {pallet[2] && pallet[2].length !== 0 && (
          <div
            className={`pallet pallet-double ${getPalletColor(
              blocksCount[pallet[2][0].location][pallet[2][0].row][
                pallet[2][0].palletPosition
              ]
            )} ${highlight(pallet[2])}`}
          >
            <p
              className={`${rotatePalletTextBasedOnRotation(
                blocksCount[pallet[2][0].location][pallet[2][0].row][
                  pallet[2][0].palletPosition
                ].angle
              )}`}
            >
              {
                blocksCount[pallet[2][0].location][pallet[2][0].row][
                  pallet[2][0].palletPosition
                ].count
              }
            </p>
          </div>
        )}
        {pallet[3] && pallet[3].length !== 0 && (
          <div
            className={`pallet pallet-double ${getPalletColor(
              blocksCount[pallet[3][0].location][pallet[3][0].row][
                pallet[3][0].palletPosition
              ]
            )} ${highlight(pallet[3])}`}
          >
            <p
              className={`${rotatePalletTextBasedOnRotation(
                blocksCount[pallet[3][0].location][pallet[3][0].row][
                  pallet[3][0].palletPosition
                ].angle
              )}`}
            >
              {
                blocksCount[pallet[3][0].location][pallet[3][0].row][
                  pallet[3][0].palletPosition
                ].count
              }
            </p>
          </div>
        )}
      </div>
    );
  }
};

export const countBlocksUsingLPNs = (socketData) => {
  let finalObj = socketData.reduce(function (acc, currentObj, i) {
    let locationName = currentObj.location;
    let cRow = currentObj.row;
    let cPalletPosition = currentObj.palletPosition;
    if (!(locationName in acc)) {
      acc[locationName] = {
        row: {}
      };
    }
    if (!acc[locationName][cRow]) acc[locationName][cRow] = {};
    if (!acc[locationName][cRow][cPalletPosition]) {
      acc[locationName][cRow][cPalletPosition] = {
        count: 0,
        totalCount: 0
      };
    }
    if (currentObj.lpn) {
      acc[locationName][cRow][cPalletPosition]["count"] =
        acc[locationName][cRow][cPalletPosition]["count"] + 1;
    }
    acc[locationName][cRow][cPalletPosition]["totalCount"] =
      acc[locationName][cRow][cPalletPosition]["totalCount"] + 1;
    acc[locationName][cRow][cPalletPosition] = {
      ...acc[locationName][cRow][cPalletPosition],
      ...currentObj
    };
    return acc;
  }, {});
  return finalObj;
};

export const countRacksUsingLPNs = (socketData) => {
  let finalObj = socketData.reduce(function (acc, currentObj, i) {
    let locationName = currentObj.location;
    locationName = locationName.split(".").slice(0, -1).join(".");
    let cRow = currentObj.row;
    let cPalletPosition = currentObj.palletPosition;

    if (!(locationName in acc)) {
      acc[locationName] = {
        row: {}
      };
    }

    if (!acc[locationName][cRow]) acc[locationName][cRow] = {};

    if (!acc[locationName][cRow][cPalletPosition]) {
      acc[locationName][cRow][cPalletPosition] = {
        count: 0,
        totalCount: 0
      };
    }

    if (currentObj.lpn) {
      acc[locationName][cRow][cPalletPosition]["count"] =
        acc[locationName][cRow][cPalletPosition]["count"] + 1;
    }

    acc[locationName][cRow][cPalletPosition]["totalCount"] =
      acc[locationName][cRow][cPalletPosition]["totalCount"] + 1;

    acc = { ...acc, ...currentObj };
    return acc;
  }, {});
  return finalObj;
};

const renderPalletData = (palletData, blocksCount, childOfPallet) => {
  if (palletData && palletData !== "empty") {
    const { location, row, palletPosition } = palletData;
    const blockInfo =
      blocksCount?.[location]?.[row]?.[palletPosition] || palletData;

    const palletClass = `pallet pallet-double ${getPalletColor(
      blockInfo
    )} ${highlight(childOfPallet)}`;

    const textClass = rotatePalletTextBasedOnRotation(blockInfo.angle);

    return (
      <div className={palletClass.trim()}>
        <p className={textClass}>{blockInfo.count || 0}</p>
      </div>
    );
  }
};

export const renderDoublePalletLocationWithReverseOrder = (
  pallet,
  index,
  blocksCount
) => {
  const firstChildOfPallet = Object.values(pallet)[0];
  const secondChildOfPallet = Object.values(pallet)[1];
  const thirdChildOfPallet = Object.values(pallet)[2];

  const getPalletData = (pallet) => {
    if (typeof pallet !== "object" || pallet === null) return null;

    if ("location" in pallet) {
      return pallet;
    }
    for (const key in pallet) {
      const result = getPalletData(pallet[key]);
      if (result !== null) {
        return result;
      }
    }
    return null;
  };

  const firstPalletData = getPalletData(firstChildOfPallet);
  const secondPalletData = getPalletData(secondChildOfPallet);
  const thirdPalletData = getPalletData(thirdChildOfPallet);

  return (
    <div
      key={index}
      className={`${
        firstChildOfPallet && firstPalletData?.locationOrientation === "E"
          ? "pallet-col-reverse"
          : "pallet-col"
      }`}
    >
      {renderPalletData(firstPalletData, blocksCount, firstChildOfPallet)}
      {renderPalletData(secondPalletData, blocksCount, secondChildOfPallet)}
      {renderPalletData(thirdPalletData, blocksCount, thirdChildOfPallet)}
    </div>
  );
};

export const shouldRotatePallet = (angle, rackType) => {
  switch (rackType) {
    case "RACK_2":
      switch (angle) {
        case 90:
          return false;
        case -90:
          return false;
        case 180:
          return true;
        case 0:
          return true;
      }
    case "RACK":
      switch (angle) {
        case 90:
          return true;
        case -90:
          return true;
        case 180:
          return false;
        case 0:
          return false;
      }
  }
};

export const getPalletPositionOrRowCount = (totalPallets) => {
  let palletPositionGroup = {};
  totalPallets.forEach((pallet, index) => {
    if (!palletPositionGroup[pallet.palletPosition]) {
      palletPositionGroup[pallet.palletPosition] = [];
      palletPositionGroup[pallet.palletPosition].push(pallet);
    } else {
      palletPositionGroup[pallet.palletPosition].push(pallet);
    }
  });
  return palletPositionGroup;
};

export const getRackType = (rackData) => {
  const [firstPalletOfLocation] = rackData;
  if (firstPalletOfLocation) {
    const { rackType } = firstPalletOfLocation;
    return rackType ? rackType : null;
  }
};

export const groupRackWithRowAsKey = (rackData) => {
  let rackGroupWithRow = {};
  rackData.forEach((pallet) => {
    if (!rackGroupWithRow[pallet.row]) {
      rackGroupWithRow[pallet.row] = [];
    }
    rackGroupWithRow[pallet.row].push(pallet);
  });
  return rackGroupWithRow;
};

const groupRackWithPalletPositionAsKey = (rackData) => {
  let rackGroupWithPalletPosition = {};
  rackData.forEach((pallet) => {
    if (!rackGroupWithPalletPosition[pallet.palletPosition]) {
      rackGroupWithPalletPosition[pallet.palletPosition] = [];
    }
    rackGroupWithPalletPosition[pallet.palletPosition].push(pallet);
  });
  return rackGroupWithPalletPosition;
};

export const getMetaDataOfRackLocation = (rackData) =>
  Object.values(rackData)[0] ? Object.values(rackData)[0] : null;

export const getRacksCountByLocation = (locationMetaData, racksCount) => {
  const locationName = stripLastPartFromString(locationMetaData?.location);

  return (
    racksCount?.[locationName]?.[locationMetaData.row]?.[
      locationMetaData.palletPosition
    ] || {
      count: 0,
      totalCount: 0
    }
  );
};

export const stripLastPartFromString = (location) => {
  return location?.split(".").slice(0, -1).join(".");
};

const renderPalletPosition = (
  isPalletRotated,
  locationMetaData = {},
  racksCount,
  color,
  allPositions
) => {
  return (
    <div
      className={`double-deep-rack-size  
        ${color}${highlight(allPositions)}`}
    >
      <p
        className={`pallet-text-size ${
          !isPalletRotated ? "rotate-text-double-deep-rack" : ""
        }`}
      >
        {getRacksCountByLocation(locationMetaData, racksCount)?.count}
      </p>
    </div>
  );
};

const getRackPositionWithOrientation = (
  firstPosition,
  secondPosition,
  thirdPosition,
  locationMetaData,
  isPalletRotated,
  racksCount,
  renderList = []
) => {
  let locationOrientation = locationMetaData?.locationOrientation;
  const plantMetaData = store.getState().plant.plantDetails.plantMetaData;

  if (!plantMetaData?.length) return null;

  const layoutOrientation = plantMetaData?.find(
    (configItem) => configItem.configName === "LAYOUT_ORIENTATION"
  );
  const { configValue: plantOrientation } = layoutOrientation || {};

  // Function to render the pallet positions
  const renderPositions = (positions) =>
    positions?.map((position, index) => {
      if (!position) return;
      const sortedColorArr = position
        .map((locMetaData) => {
          return getPalletColor({
            ...getRacksCountByLocation(locMetaData, racksCount),
            ...locMetaData
          });
        })
        .sort((a, b) => {
          return rackColorSortOrder.indexOf(a) - rackColorSortOrder.indexOf(b);
        });

      return renderPalletPosition(
        isPalletRotated,
        positions?.[index]?.[0],
        racksCount,
        sortedColorArr[0],
        positions?.[index]
      );
    });

  // Function to get the correct CSS classes
  const getPalletClass = () => {
    return `pallet pallet-rack-single`;
  };

  // Define rack types and orientations
  const orientationConditions = {
    N: ["S", "E"], // Positions for N
    S: ["S", "E"] // Positions for S
  };

  // Determine if we should render based on layout orientation
  const shouldRenderFirstToLast =
    orientationConditions[plantOrientation].includes(locationOrientation);

  const positions = !shouldRenderFirstToLast
    ? [firstPosition, secondPosition, thirdPosition]
    : [thirdPosition, secondPosition, firstPosition];

  return <div className={getPalletClass()}>{renderPositions(positions)}</div>;
};

export const renderRackPallet = (renderList, racksCount) => {
  if (renderList) {
    const rackTypeOfThisLocation = getRackType(renderList[0]);
    const locationMetaData = getMetaDataOfRackLocation(renderList[0]);
    const isPalletRotated = shouldRotatePallet(
      locationMetaData.angle,
      locationMetaData.rackType
    );

    switch (rackTypeOfThisLocation) {
      case "RACK":
        //Single Deep Racks
        const singleRackGroupedData = groupRackWithRowAsKey(renderList[0]);

        //Available Rows in the Single Deep Rack location
        const [firstRowOfPallet, secondRowOfPallet, thirdRowOfPallet] =
          Object.values(singleRackGroupedData);

        return (
          <div
            className={`pallets-wrapper-col ${
              !isPalletRotated && "rotate-rack"
            }`}
          >
            <div
              className={`${
                locationMetaData.locationOrientation === "E"
                  ? "pallet-col-reverse"
                  : "pallet-col"
              }`}
            >
              {getRackPositionWithOrientation(
                firstRowOfPallet,
                secondRowOfPallet,
                thirdRowOfPallet,
                locationMetaData,
                isPalletRotated,
                racksCount,
                renderList?.[0]
              )}
            </div>
          </div>
        );
      case "RACK_2":
        //Double Deep Racks
        const doubleRackGroupedData = groupRackWithPalletPositionAsKey(
          renderList[0]
        );

        //Available Pallet Position in the Double Deep Rack Location
        const [
          firstPositionOfPallet,
          secondPositionOfPallet,
          thirdPositionOfPallet
        ] = Object.values(doubleRackGroupedData);

        return (
          <div
            className={`pallets-wrapper-col ${
              !isPalletRotated && "rotate-rack"
            }`}
          >
            <div
              className={`${
                locationMetaData.locationOrientation === "E"
                  ? "pallet-col-reverse"
                  : "pallet-col"
              }`}
            >
              {getRackPositionWithOrientation(
                firstPositionOfPallet,
                secondPositionOfPallet,
                thirdPositionOfPallet,
                locationMetaData,
                isPalletRotated,
                racksCount,
                renderList?.[0]
              )}
            </div>
          </div>
        );
      default:
        return <></>;
    }
  }
};

export const renderPreFilledSingleBlockPallet = (
  renderList,
  isLocationVertical,
  blocksCount
) => {
  const preFilledRenderList =
    normalizeAndPreFillEmptyPalletPosition(renderList);

  const finalRenderList = renderList[0]?.[0] ? renderList[0] : renderList;

  return (
    finalRenderList && (
      <div
        className={`pallets-wrapper-row 
      `}
      >
        {Object.values(finalRenderList).map((pallet, index) => {
          if (pallet) {
            return renderDoublePalletLocationWithReverseOrder(
              pallet,
              index,
              blocksCount
            );
          }
        })}
      </div>
    )
  );
};

export const rotateMarker = (
  angle,
  locationOrientation,
  positionOrientation
) => {
  if (angle == 180) return 0;
  if (positionOrientation == "W" && angle == -90) return 90;
  if (positionOrientation == "E" && angle == 90) return -90;
  return angle;
};

const getRotationOfMarker = (isLocationVertical, locationMetaData) => {
  if (isLocationVertical) {
    if (locationMetaData.positionOrientation == "E") {
      return "rotate-rack-90";
    }
    return "rotate-rack";
  }
  return "";
};

/**
 *
 * NOTE:
 * !!!!!!!-For Margin Offset of Single Stack Pallet, providing value in multiples of 5 is recommended---!!!!!!!
 * @returns
 */
export const renderPreFilledDoubleBlockPallet = (
  sectionsData,
  isLocationVertical,
  locationMetaData,
  blocksCount,
  marker
) => {
  let maxRowCount = 2;
  for (const section of sectionsData) {
    for (const pallet of section) {
      if (pallet && typeof pallet === "object" && !pallet.palletStatus) {
        const rowKeys = Object.keys(pallet).map(Number).filter((n) => !isNaN(n));
        if (rowKeys.length) {
          const max = Math.max(...rowKeys);
          if (max > maxRowCount) maxRowCount = max;
        }
      }
    }
  }

  return (
    <div className={`pallets-wrapper-col`}>
      {sectionsData.map((sectionDouble, index) => {
        if (sectionDouble) {
          let totalDoubleRowsOfLocation = locationMetaData.doubleRows;
          let reversedArray = sectionDouble.reduce(
            (acc, item) => [item].concat(acc),
            []
          );

          const firstObject = sectionDouble[0];
          const firstInnerObject = Object.values(firstObject)[0];
          const secondInnerObject = Object.values(firstObject)[1];
          const firstNestedObject = Object.values(firstInnerObject)[0];

          //PreFill reversedArray data here
          let preFilledReversedPalletList = preFillPalletPositionIfEmpty(
            reversedArray,
            index
          );
          if (locationMetaData.positionOrientation == "N") {
            if (index < locationMetaData.pbcRows) {
              return (
                reversedArray && (
                  <span className="single-pallet-wrapper">
                    <div
                      key={index}
                      className={`${
                        firstNestedObject.locationOrientation === "E"
                          ? `pallets-wrapper-single-row-reverse`
                          : `pallets-wrapper-single-row`
                      } ${replaceDotWithDash(locationMetaData.location)}`}
                    >
                      {Object.values(reversedArray).map((pallet, index) => {
                        //Render Single Pallet
                        return renderSinglePalletLocation(
                          pallet,
                          index,
                          blocksCount,
                          maxRowCount
                        );
                      })}
                    </div>
                  </span>
                )
              );
            }
            return (
              reversedArray && (
                <div
                  key={index}
                  className={`${
                    firstInnerObject?.[0].locationOrientation === "E" ||
                    secondInnerObject?.[0].locationOrientation === "E"
                      ? "pallets-wrapper-row-reverse"
                      : "pallets-wrapper-row"
                  }`}
                >
                  {Object.values(reversedArray).map((pallet, index) => {
                    //Render Double Pallet
                    return renderDoublePalletLocation(
                      pallet,
                      index,
                      blocksCount,
                      maxRowCount
                    );
                  })}
                </div>
              )
            );
          }
          if (index + 1 > totalDoubleRowsOfLocation) {
            return (
              reversedArray && (
                <span className="single-pallet-wrapper">
                  <div
                    key={index}
                    className={`${
                      firstNestedObject.locationOrientation === "E"
                        ? `pallets-wrapper-single-row-reverse`
                        : `pallets-wrapper-single-row`
                    } ${replaceDotWithDash(locationMetaData.location)}`}
                  >
                    {Object.values(reversedArray).map((pallet, index) => {
                      //Render Single Pallet
                      return renderSinglePalletLocation(
                        pallet,
                        index,
                        blocksCount,
                        maxRowCount
                      );
                    })}
                  </div>
                </span>
              )
            );
          } else {
            return (
              reversedArray && (
                <div
                  key={index}
                  className={`${
                    firstInnerObject?.[0].locationOrientation === "E" ||
                    secondInnerObject?.[0].locationOrientation === "E"
                      ? "pallets-wrapper-row-reverse"
                      : "pallets-wrapper-row"
                  }`}
                >
                  {Object.values(reversedArray).map((pallet, index) => {
                    //Render Double Pallet
                    return renderDoublePalletLocation(
                      pallet,
                      index,
                      blocksCount,
                      maxRowCount
                    );
                  })}
                </div>
              )
            );
          }
        }
      })}
    </div>
  );
};

export const getBlockRackDataWithLocationOrientation = (
  locationOrientation,
  sectionsData,
  type
) => {
  switch (locationOrientation) {
    case "S":
    case "W":
      return type === "block" ? sectionsData?.[0] : sectionsData;
    case "N":
    case "E":
      return type === "block"
        ? sectionsData?.[0].reduce((acc, item) => [item].concat(acc), [])
        : sectionsData;
    default:
      return sectionsData;
  }
};

export const getLocationOrientationOfStorage = (type, sectionsData) => {
  if (sectionsData[0] !== undefined) {
    const [firstArrayItem] = sectionsData[0];
    const [firstChildOfPallet] = Object.values(firstArrayItem);
    if (type === "block") {
      if (firstArrayItem && firstChildOfPallet) {
        return firstChildOfPallet.locationOrientation
          ? firstChildOfPallet.locationOrientation
          : firstChildOfPallet[0].locationOrientation;
      }
    }
    return firstArrayItem.locationOrientation;
  }
};

export const renderDoubleEmptyPalletLocation = (index, rowCount = 2) => {
  return (
    <div key={index} className="pallet-col">
      <div className={`pallet pallet-double empty-pallet`}>
        <p>0</p>
      </div>
      <div className={`pallet pallet-double empty-pallet`}>
        <p>0</p>
      </div>
      {rowCount >= 3 && (
        <div className={`pallet pallet-double empty-pallet`}>
          <p>0</p>
        </div>
      )}
    </div>
  );
};

export const renderSingleEmptyRowPalletLocation = (index, rowCount = 2) => {
  return (
    <div key={index} className="pallet-col">
      <div className={`pallet pallet-single empty-pallet`}>
        <p>0</p>
      </div>
      <div className={`pallet pallet-single empty-pallet`}>
        <p>0</p>
      </div>
      {rowCount >= 3 && (
        <div className={`pallet pallet-single empty-pallet`}>
          <p>0</p>
        </div>
      )}
    </div>
  );
};

export const renderDoubleEmptyColumnPalletLocation = (sectionSingle, index) => {
  return (
    <div
      key={index}
      className={`${
        sectionSingle[1][0].locationOrientation === "E"
          ? "pallet-col-reverse"
          : "pallet-col"
      }`}
    >
      <div className={`pallet pallet-single empty-pallet`}>
        {sectionSingle[1][0].palletLevel}
      </div>
      {sectionSingle[2] && sectionSingle[2][0] && (
        <div className={`pallet pallet-single empty-pallet`}>
          {sectionSingle[2][0].palletLevel}
        </div>
      )}
      {sectionSingle[3] && sectionSingle[3][0] && (
        <div className={`pallet pallet-single empty-pallet`}>
          {sectionSingle[3][0].palletLevel}
        </div>
      )}
    </div>
  );
};
// --RollBack if needed
export const findIsLocationVertical = (renderList) => {
  let angle;

  if (renderList?.[0]) {
    if (renderList[0][1] && renderList[0][1][0]) {
      angle = renderList[0][1][0].angle;
    } else if (renderList[0][2] && renderList[0][2][0]) {
      angle = renderList[0][2][0].angle;
    } else if (renderList[0][3] && renderList[0][3][0]) {
      angle = renderList[0][3][0].angle;
    }
  }
  if (angle == 90 || angle == -90) {
    return true;
  }
};

export const formatName = (locationName) => {
  const splittedString = locationName.split(".");
  const result = splittedString.slice(0, splittedString.length - 1);
  return result.join(".");
};

export const getBlockRackName = (modalType, locationName) => {
  if (modalType) {
    let splittedName = locationName.split(".");
    return splittedName.slice(0, splittedName.length - 1).join(".");
  } else {
    return locationName;
  }
};

export const getLocationGroupOffset = (convertedValue, offsetObject) => {
  if (convertedValue && offsetObject) {
    //Offset Object
    let offsetCoordinates = {
      x: convertedValue.lat + offsetObject.x,
      y: convertedValue.lng + offsetObject.y
    };
    //Offset Array
    let offsetCoordinateArray = [offsetCoordinates.x, offsetCoordinates.y];
    return offsetCoordinateArray;
  }
};

export const getDestinationPointerOffset = (
  { currentPos, destinationCurrentPos },
  offsetObject
) => {
  if ((currentPos, destinationCurrentPos)) {
    let sourceOffset = [
      currentPos.lat + offsetObject.coordinateCorrection.source.x,
      currentPos.lng + offsetObject.coordinateCorrection.source.y
    ];

    let destinationOffset = [
      destinationCurrentPos.lat + offsetObject.value.x,
      destinationCurrentPos.lng + offsetObject.value.y
    ];

    return [sourceOffset, destinationOffset];
  }
};

export const getConvertedLatLng = (
  longitude,
  latitude,
  bounds,
  mapObject,
  lgvOneObject = "lgv"
) => {
  const { x, y } = mapCoordinate(longitude, latitude, lgvOneObject, bounds);
  const point = L.point(x, y);
  return mapObject.map.target.layerPointToLatLng(point);
};

export const getDestinationPointerOffsetV2 = (
  { currentPos, destinationCurrentPos },
  coordinateCorrection,
  destinationCorrection = {}, // default to empty object
  locationPointingTo
) => {
  if (currentPos && destinationCurrentPos) {
    const sourceOffset = [
      currentPos.lat + coordinateCorrection.source.x,
      currentPos.lng + coordinateCorrection.source.y
    ];

    let matchedKey = null;

    if (
      typeof destinationCorrection === "object" &&
      destinationCorrection !== null
    ) {
      matchedKey = Object.keys(destinationCorrection).find((key) =>
        locationPointingTo.includes(key)
      );
    }

    const destinationCorrectionOffset =
      (matchedKey && destinationCorrection[matchedKey]) ??
      coordinateCorrection.destination;

    const destinationOffset = [
      destinationCurrentPos.lat + destinationCorrectionOffset.x,
      destinationCurrentPos.lng + destinationCorrectionOffset.y
    ];

    return [sourceOffset, destinationOffset];
  }

  return [null, null];
};

export const getPlantMapImageUrl = (config, locationCode, imageType) => {
  const { WAREHOUSE_CONTAINER_URI, YARD_CONTAINER_URI } = config;
  if (!locationCode && !imageType) return "";

  switch (imageType) {
    case "YARD_MAP":
      return `${getBlobStorageEndPoint(
        config
      )}${YARD_CONTAINER_URI}${locationCode}.avif`;
    default:
      return `${getBlobStorageEndPoint(
        config
      )}${WAREHOUSE_CONTAINER_URI}${locationCode}.avif`;
  }
};

export const getUserAuthPayload = (userData) => {
  if (!userData) return {};

  let payload = {};
  const { oid, name, email } = userData;
  Object.assign(payload, userDataObject);
  payload.mail = email;
  payload.displayName = name;
  payload.id = oid;
  return payload;
};

//accesing root element
const root = document.documentElement;

export const setBlockStorageSize = (blockSize) => {
  const { blockWidth, blockHeight, singleStackWidth, singleStackHeight } =
    blockSize;
  //Set Block Storage Height and Width
  root.style.setProperty("--block-storage-width", blockWidth);
  root.style.setProperty("--block-storage-height", blockHeight);

  //Set Block Single Stack Height and Width
  root.style.setProperty(
    "--block-single-stack-storage-width",
    singleStackWidth
  );
  root.style.setProperty(
    "--block-single-stack-storage-height",
    singleStackHeight
  );

  //Set Block Single Stack Start Margin
};

export const setVerticalBlockStorageSize = (blockSize) => {
  const { blockWidth, blockHeight, singleStackWidth, singleStackHeight } =
    blockSize;
  //Set Block Storage Height and Width
  root.style.setProperty("--vblock-storage-width", blockWidth);
  root.style.setProperty("--vblock-storage-height", blockHeight);

  //Set Block Single Stack Height and Width
  root.style.setProperty(
    "--vblock-single-stack-storage-width",
    singleStackWidth
  );
  root.style.setProperty(
    "--vblock-single-stack-storage-height",
    singleStackHeight
  );

  //Set Block Single Stack Start Margin
};

export const setRackStorageSize = (rackSize) => {
  const { rackWidth, rackHeight } = rackSize;
  //Set Block Storage Height and Width
  root.style.setProperty("--rack-storage-width", rackWidth);
  root.style.setProperty("--rack-storage-height", rackHeight);
};

export const getPbcAndDoubleRowMaxCount = (locationMetaData) => {
  const { doubleRows, pbcRows, totalPositions, transitionPallet } =
    locationMetaData;
  let maxPbcRowCount = transitionPallet;
  let maxDoubleRowCount = totalPositions / 2 - transitionPallet / doubleRows;

  return { pbc: maxPbcRowCount, double: maxDoubleRowCount };
};

export const isPbcRowAtTop = (locationMetaData) => {
  const { positionOrientation } = locationMetaData;
  return positionOrientation === "S" ? true : false;
};

export const shiftPalletData = (palletsList) => {
  let sectionList = structuredClone(palletsList);
  let count = sectionList.length;

  for (let i = 0; i < Math.floor(count / 2); i++) {
    let oppIndex = count - 1 - i;
    let minLength = Math.min(
      sectionList[i].length,
      sectionList[oppIndex].length
    );

    for (let j = 0; j < minLength; j++) {
      [sectionList[i][j], sectionList[oppIndex][j]] = [
        sectionList[oppIndex][j],
        sectionList[i][j]
      ];
    }
  }

  return sectionList;
};

export const isBoundsValid = (bounds, maxBounds) => {
  if (
    bounds.startY >= maxBounds.startX &&
    bounds.endY <= maxBounds.endX &&
    bounds.startX >= maxBounds.startX &&
    bounds.endX <= maxBounds.endY
  ) {
    return true;
  }
  return false;
};

export const getTimeBasedOnUserTime = (plantTimeStamp) => {
  if (!plantTimeStamp) return "";
  let [date, time] = plantTimeStamp.split("T");
  time = time.split(":");
  time = `${time[0]}:${time[1]}:${time[2].split(".")[0]}`;
  const timeZone = store.getState().plant.plantDetails.plantTimeZone;
  const timeOfPlant = momentTZ.tz(`${date} ${time}`, timeZone);
  const timeOfUser = timeOfPlant
    .clone()
    .tz(Intl.DateTimeFormat().resolvedOptions().timeZone);
  // returning raw time, format it accordingly
  return timeOfUser;
};

export const formatSeconds = (secondsStr) => {
  const totalSeconds = parseInt(secondsStr, 10);

  if (isNaN(totalSeconds)) return "";

  const duration = momentTZ.duration(totalSeconds, "seconds");
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes().toString().padStart(2, "0");
  const seconds = duration.seconds().toString().padStart(2, "0");
  const hourSuffix = hours > 1 ? "hrs+" : "hr+";

  return hours > 0
    ? `${hours.toString()} ${hourSuffix}`
    : `${minutes}m ${seconds}s`;
};

export const extractUniqueFieldValues = (data, field) => {
  const result = new Set();

  data.forEach((el) => {
    if (field === "sku" && el.sku_1 && el.sku_2) {
      result.add(el.sku_1);
      result.add(el.sku_2);
    } else if (field === "lpn" && el.lpn_1 && el.lpn_2) {
      result.add(el.lpn_1);
      result.add(el.lpn_2);
    } else if (field === "status") {
      const value =
        el[field] || el["quality"] || el["qualityStatus"] || el["palletStatus"];
      result.add(value);
    } else {
      const value = el[field] || el["dock"] || el["vehicleNumber"];
      if (Array.isArray(value)) {
        value.forEach((v) => v && result.add(v));
      } else if (value) {
        result.add(value);
      }
    }
  });

  return Array.from(result)
    .map((value) => ({ [field]: value }))
    .sort((a, b) => a[field].localeCompare(b[field]));
};

/**
 * Returing Meta data of a single location
 * @param {*} location-object
 * @returns metaData-object
 */
export const getMetaDataOfLocation = (
  {
    latitude,
    longitude,
    location,
    totalPositions,
    positionTransition,
    positionOrientation,
    locationOrientation,
    positionLength
  },
  type
) => ({
  latitude,
  longitude,
  location,
  totalPositions,
  locationOrientation,
  positionLength,
  ...(type === "block" && {
    positionTransition,
    positionOrientation
  })
});

export const markerSectionGenerator = (
  locationsList,
  transitionPallet,
  doubleRows,
  pbcRows,
  totalPositions,
  locationMetaData,
  isExceptional
) => {
  let sectionList = [];

  //Rack List
  let racksList = [];

  //Block List
  //Single Column List
  let blockSingleColumnList = [];

  //Double Rows List
  let doubleList1 = [];
  let doubleList2 = [];
  let doubleList3 = [];
  let doubleList4 = [];

  //PBC Rows List
  let pbcList1 = [];
  let pbcList2 = [];
  let pbcList3 = [];
  let pbcList4 = [];

  let totalDoubleRows;
  let palletsInDoubleRow;
  let totalPbcRows;
  let pbcRowsStartPalletIndex;
  let palletsInPbcRow;

  if (transitionPallet === undefined) {
    //Racks
    Object.values(locationsList).forEach((location) => {
      racksList.push(location);
    });

    sectionList.push(racksList);
  } else {
    //Blocks
    //Double Rows
    totalDoubleRows = doubleRows;

    //Pallets for Double Rows
    // if (pbcRows === null) {
    //   palletsInDoubleRow = transitionPallet;
    // } else {
    //   palletsInDoubleRow =
    //     (totalPositions / 2 - transitionPallet) / totalDoubleRows;
    // }

    //PBC Rows
    totalPbcRows = pbcRows;
    //PBC Rows Start Pallet Index
    pbcRowsStartPalletIndex = totalPositions / 2 - transitionPallet;
    //Pallets for Pbc Rows
    palletsInPbcRow = (totalPositions / 2 - transitionPallet) / totalPbcRows;

    //Required values
    let pbcToDoubleTransition;
    palletsInDoubleRow = Math.round(
      (totalPositions / 2 - transitionPallet) / totalDoubleRows
    );
    if (pbcRows >= 2) {
      pbcToDoubleTransition = transitionPallet - 1;
    } else {
      pbcToDoubleTransition = transitionPallet * pbcRows - 1;
    }
    Object.values(locationsList).forEach((location, index) => {
      if (transitionPallet === null) {
        blockSingleColumnList.push(location);
      } else {
        if (pbcRows !== null) {
          let pbcTransition = Math.round(transitionPallet / pbcRows);
          //PBC Rows
          if (index <= pbcToDoubleTransition) {
            if (totalPbcRows === 1) {
              if (
                index <=
                getMultiplesOfPbcTransitionPallet(pbcTransition, 1) - 1
              ) {
                pbcList1.push(location);
              }
            } else if (totalPbcRows === 2) {
              if (
                index <=
                getMultiplesOfPbcTransitionPallet(pbcTransition, 1) - 1
              ) {
                pbcList1.push(location);
              } else if (
                index >= getMultiplesOfPbcTransitionPallet(pbcTransition, 1) &&
                index <= getMultiplesOfPbcTransitionPallet(pbcTransition, 2) - 1
              ) {
                pbcList2.push(location);
              }
            } else if (totalPbcRows === 3) {
              if (
                index <=
                getMultiplesOfPbcTransitionPallet(pbcTransition, 1) - 1
              ) {
                pbcList1.push(location);
              } else if (
                index >=
                  getMultiplesOfPbcTransitionPallet(pbcTransition, 1) + 1 &&
                index <= getMultiplesOfPbcTransitionPallet(pbcTransition, 2) - 1
              ) {
                pbcList2.push(location);
              } else if (
                index >=
                  getMultiplesOfPbcTransitionPallet(pbcTransition, 2) + 1 &&
                index <= getMultiplesOfPbcTransitionPallet(pbcTransition, 3) - 1
              ) {
                pbcList2.push(location);
              }
            } else if (totalPbcRows === 4) {
              if (
                index <=
                getMultiplesOfPbcTransitionPallet(pbcTransition, 1) - 1
              ) {
                pbcList1.push(location);
              } else if (
                index >=
                  getMultiplesOfPbcTransitionPallet(pbcTransition, 1) + 1 &&
                index <= getMultiplesOfPbcTransitionPallet(pbcTransition, 2) - 1
              ) {
                pbcList2.push(location);
              } else if (
                index <=
                  getMultiplesOfPbcTransitionPallet(pbcTransition, 2) + 1 &&
                index <= getMultiplesOfPbcTransitionPallet(pbcTransition, 3) - 1
              ) {
                pbcList3.push(location);
              } else if (
                index <=
                  getMultiplesOfPbcTransitionPallet(pbcTransition, 3) + 1 &&
                index <= getMultiplesOfPbcTransitionPallet(pbcTransition, 4) - 1
              ) {
                pbcList4.push(location);
              }
            }
          }
          //Double Rows
          else if (index >= pbcToDoubleTransition) {
            if (totalDoubleRows === 1) {
              if (
                index >= transitionPallet * pbcRows &&
                index <=
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    1
                  ) -
                    1
              ) {
                doubleList1.push(location);
              }
            } else if (totalDoubleRows === 2) {
              if (
                index >= transitionPallet / pbcRows - 1 &&
                index <=
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    1
                  ) -
                    1
              ) {
                doubleList1.push(location);
              } else if (
                index >
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    1
                  ) -
                    1 &&
                index <=
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    2
                  )
              ) {
                doubleList2.push(location);
              }
            } else if (totalDoubleRows === 3) {
              if (
                index >= Math.floor(transitionPallet / pbcRows) &&
                index <=
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    1
                  ) -
                    1
              ) {
                doubleList1.push(location);
              } else if (
                index >=
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    1
                  ) &&
                index <=
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    2
                  ) -
                    1
              ) {
                doubleList2.push(location);
              } else if (
                index >=
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    2
                  ) &&
                index <=
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    3
                  )
              ) {
                doubleList3.push(location);
              }
            } else if (totalDoubleRows === 4) {
              if (
                index >= transitionPallet * pbcRows &&
                index <=
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    1
                  ) -
                    1
              ) {
                doubleList1.push(location);
              } else if (
                index >=
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    1
                  ) &&
                index <=
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    2
                  ) -
                    1
              ) {
                doubleList2.push(location);
              } else if (
                index >=
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    2
                  ) &&
                index <=
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    3
                  ) -
                    1
              ) {
                doubleList3.push(location);
              } else if (
                index >=
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    3
                  ) &&
                index <=
                  getMultiplesOfTransitionPallet(
                    transitionPallet,
                    palletsInDoubleRow,
                    4
                  ) -
                    1
              ) {
                doubleList4.push(location);
              }
            }
          }
        } else {
          if (index <= palletsInDoubleRow - 1) {
            if (doubleRows == 1) {
              if (index <= palletsInDoubleRow * 2) {
                doubleList1.push(location);
              }
            } else if (doubleRows === 2) {
              if (index <= palletsInDoubleRow * 2 - 1) {
                doubleList1.push(location);
              } else if (
                index >= palletsInDoubleRow * 2 &&
                index <= palletsInDoubleRow * 3
              ) {
                doubleList2.push(location);
              }
            } else if (doubleRows === 3) {
              if (index <= palletsInDoubleRow * 2 - 1) {
                doubleList1.push(location);
              } else if (
                index >= palletsInDoubleRow * 2 &&
                index <= palletsInDoubleRow * 3 - 1
              ) {
                doubleList2.push(location);
              } else if (
                index >= palletsInDoubleRow * 3 &&
                index <= palletsInDoubleRow * 4 - 1
              ) {
                doubleList3.push(location);
              }
            } else if (doubleRows === 4) {
              if (index <= palletsInDoubleRow * 2 - 1) {
                doubleList1.push(location);
              } else if (
                index >= palletsInDoubleRow * 2 &&
                index <= palletsInDoubleRow * 3 - 1
              ) {
                doubleList2.push(location);
              } else if (
                index >= palletsInDoubleRow * 3 &&
                index <= palletsInDoubleRow * 4 - 1
              ) {
                doubleList3.push(location);
              } else if (
                index >= palletsInDoubleRow * 4 &&
                index <= palletsInDoubleRow * 5 - 1
              ) {
                doubleList3.push(location);
              }
            }
          }
        }
      }
    });

    const { locationOrientation } = locationMetaData;
    // Combining All Pallet Lists
    if (transitionPallet === null) {
      sectionList.push(blockSingleColumnList);
    } else {
      const allLists = [
        pbcList1,
        pbcList2,
        pbcList3,
        pbcList4,
        doubleList1,
        doubleList2,
        doubleList3,
        doubleList4
      ];

      allLists.forEach(list => {
        if (list.length) {
          sectionList.push(list.reverse());
        }
      });
    }

    const reversedList = [];
    if (locationOrientation === 'S') {
      if(isExceptional !== true) {
        sectionList.forEach((item) => {
          const temp = item.reverse();
          reversedList.push(temp);
        });
  
        sectionList = reversedList;
      }
    }

  }

  return sectionList;
};

export const getMultiplesOfPbcTransitionPallet = (
  palletTransition,
  multiples
) => {
  return palletTransition * multiples;
};

export const getMultiplesOfTransitionPallet = (
  palletTransition,
  palletsInDoubleRow,
  multiples
) => {
  return palletTransition + palletsInDoubleRow * multiples;
};

export const GenerateHTMLBlockIcon = ({
  locationMetaData,
  locationsList,
  type,
  blocksCount,
  racksCount,
  marker,
  isExceptional
}) => {
  if (locationMetaData.totalPositions !== 0) {
    const transitionPallet = locationMetaData.positionTransition;

    const sectionsData = markerSectionGenerator(
      locationsList,
      transitionPallet,
      locationMetaData.doubleRows,
      locationMetaData.pbcRows,
      locationMetaData.totalPositions,
      locationMetaData,
      isExceptional
    );

    //Find the Location Orientation of the selected location
    const locationOrientationOfThisLocation = getLocationOrientationOfStorage(
      type,
      sectionsData
    );

    //Reverse or keep the list straight according to the location Orientation of the selected location
    let renderList;

    if (type === "rack") {
      //Get Rendering Data according to position orientation
      renderList = getBlockRackDataWithLocationOrientation(
        locationOrientationOfThisLocation,
        sectionsData,
        "rack"
      );

      //Render Double and Single Pallet Rack
      return renderRackPallet(renderList, racksCount);
    } else if (type === "block") {
      //Get Rendering Data according to position orientation
      renderList = getBlockRackDataWithLocationOrientation(
        locationOrientationOfThisLocation,
        sectionsData,
        "block"
      );
      //Check is the block Vertical
      let isLocationVertical = findIsLocationVertical(renderList);
      if (
        locationMetaData.positionOrientation === "S" || 
        locationMetaData.positionOrientation === "E" 
        || locationMetaData.positionOrientation === "W"
      ) {
        //Reverse the SectionData list to render the PBC Rows at bottom according to Position Orientation
        sectionsData.reverse();
      }
      return sectionsData.length === 1
        ? renderPreFilledSingleBlockPallet(
            renderList,
            isLocationVertical,
            blocksCount
          )
        : renderPreFilledDoubleBlockPallet(
            sectionsData,
            isLocationVertical,
            locationMetaData,
            blocksCount,
            marker
          );
    }
  }
};

//indoormap & yardview scrollzoom handler
const stepThreshold = 15; // for controlling scroll sensitivity
const maxZoomDelta = 1;

export const handleScrollZoom = ({
  event,
  accumulatedDeltaRef, //ref state
  zoomScales,
  setZoomLevel
}) => {
  event.preventDefault();

  const delta =
    event.deltaMode === 1 ? event.deltaY * stepThreshold : event.deltaY;

  accumulatedDeltaRef.current += delta;

  if (Math.abs(accumulatedDeltaRef.current) >= stepThreshold) {
    const direction = Math.sign(accumulatedDeltaRef.current);

    setZoomLevel((prev) => {
      let newZoomLevel = prev;

      if (direction < 0 && prev < zoomScales.length - 1) {
        newZoomLevel =
          prev + Math.min(maxZoomDelta, zoomScales.length - prev - 1);
      }

      if (direction > 0 && prev > 0) {
        newZoomLevel = prev - Math.min(maxZoomDelta, prev);
      }

      accumulatedDeltaRef.current = 0;
      return newZoomLevel;
    });
  }
};

export const openInNewTab = (path) => {
  window.open(path, "_blank", "noopener,noreferrer");
};

export const isEmptyString = (str) => {
  return typeof str === "string" && str.trim().length === 0;
};

//wildcard string checker ; fallbacks to normal if no % is provided
export const matchesWithWildcard = (userInput, target) => {
  if (typeof userInput !== "string" || typeof target !== "string") return false;

  if (userInput.includes("%")) {
    //  user input => regex
    const pattern = userInput
      .replace(/[.*+?^${}()|[\]\\]/g, "\\$&") // Escape regex special chars
      .replace(/%/g, ".*");

    const regex = new RegExp(`^${pattern}$`, "i"); //case-insensitive
    return regex.test(target);
  } else {
    return target.toLowerCase().includes(userInput.toLowerCase());
  }
};

export const closeParticularMinimizedModal = ({
  list,
  item,
  type,
  setMinimizedWebObjectsList,
  feature
}) => {
  if (!Array.isArray(list) || list.length === 0 || !item || !type) return;

  const getItemId = (entry) => {
    if (!entry) return null;

    switch (type) {
      case "docks":
        return entry?.dock;
      case "lgv":
        return entry?.vehicleNumber;
      case "productionLine":
        return entry?.LINE_NAME;
      default:
        return null;
    }
  };

  const itemToCompare = type === "docks" ? item?.[0] : item;
  const itemId = getItemId(itemToCompare);

  if (!itemId) {
    console.warn(
      `Minimize modal removal failed — invalid itemId for type: ${type}`
    );
    return;
  }

  const updatedList = list.filter((entry) => {
    const target = type === "docks" ? entry?.[0] : entry;
    return getItemId(target) !== itemId;
  });

  setMinimizedWebObjectsList((prev) => ({
    ...prev,
    [feature]: {
      ...prev[feature],
      [type]: updatedList
    }
  }));
};

export const checkFeatureEnabled = (plantConfig, feature) => {
  if (plantConfig?.[feature]?.isEnabled) return true;
  return false;
};

export const getDockPosition = (dockPosition, isHorizontal) => {
  if (isHorizontal) {
    if (dockPosition === "UP") return "dock-east-position";
    if (dockPosition === "DOWN") return "dock-west-position";
  }

  if (dockPosition === "DOWN") return "dock-south-position";
  if (dockPosition === "UP") return "dock-north-position";

  return "";
};

//token fetcher
export const getAccessTokenWithScope = async (instance, account) => {
  if (!account) return null;
  const config = {
    account,
    scopes: msalConfig.scopes
  };
  try {
    const response = await instance.acquireTokenSilent(config);
    return {
      accessToken: response.accessToken,
      expiryTime: response.expiresOn
    };
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      console.error("InteractionRequiredAuthError details:", err);
      try {
        const response = await instance.acquireTokenPopup({
          account,
          scopes: msalConfig.scopes
        });
        return {
          accessToken: response.accessToken,
          expiryTime: response.expiresOn
        };
      } catch (popupError) {
        console.error("Popup failed, redirecting to login:", popupError);
        localStorage.removeItem("auth");
        instance.loginRedirect({ scopes: msalConfig.scopes });
      }
    } else {
      console.error("Token acquisition failed:", err);
      localStorage.removeItem("auth");
      instance.loginRedirect({ scopes: msalConfig.scopes });
    }
    return null;
  }
};

export const getLgvStatusIcons = (status) => {
  const lgvStatus = {
    "Traffic": lgvYellow,
    "Alarm": lgvRed,
    Removed: lgvBlack,
    Charging: lgvLightBlue,
    Idle: lgvGreen,
    "LGV In Manual": lgvMaroon,
    "ActiveFull": lgvBlue,
    "ActiveEmpty": lgvGreen,
  };
  return lgvStatus[status];
}